<template>
  <div
    v-if="!isClosed"
    ref="banner"
    class="ws-state-banner column"
    :class="`${size} ${type}`"
  >
    <div class="ws-state-banner__inner-wrapper flex row">
      <ws-icon
        v-if="icon"
        :name="getIcon()"
        :size="24"
      />

      <div
        v-if="!$slots.default"
        class="ws-state-banner__content"
      >
        <div class="ws-state-banner__title">
          <slot name="title">
            {{ title }}
          </slot>
        </div>

        <p
          class="ws-state-banner__text"
          :class="{'ws-state-banner__text--no-title':!title && !$slots.title}"
        >
          <slot name="text">
            <ws-html :value="text" />
          </slot>
        </p>
      </div>

      <slot></slot>

      <div
        v-if="closable"
        class="ws-state-banner__closable flex justify-end"
      >
        <ws-button
          :type="WsButtonEnum.Type.GHOST"
          :size="WsButtonEnum.Size.SMALL"
          icon="close"
          @click="closeBanner"
        />
      </div>
    </div>
    <div
      v-if="button"
      class="flex justify-end"
    >
      <ws-button
        class="self-end ws-state-banner__button"
        padding="4px 8px"
        :element="WsButtonEnum.Element.A"
        :type="buttonType"
        :icon="buttonIcon"
        :href="buttonUrl"
      >
        {{ buttonLabel }}
      </ws-button>
    </div>
    <div
      v-if="$slots.buttonSlot"
      class="flex justify-end"
    >
      <slot name="buttonSlot"></slot>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { UI_SIZE, UI_VARIANT } from './WsStateBanner.enum';
import xss from 'xss';
import { WsButton, WsButtonEnum, WsIcon, WsHtml } from '@odin/components-vue2';

@Component({
  components: {
    WsHtml,
    WsIcon,
    WsButton
  },
})
export class WsStateBanner extends Vue {
  @Prop({type: String, default: UI_SIZE.LARGE}) readonly size!: UI_SIZE;
  @Prop({type: String, default: UI_VARIANT.DEFAULT}) readonly type!: UI_VARIANT;
  @Prop({type: Boolean, default: false}) readonly closable !: boolean;
  @Prop({type: Boolean, default: false}) readonly icon!: boolean;

  @Prop(String) readonly title!: string;
  @Prop(String) readonly text!: string;
  @Prop(String) readonly button!: string;
  @Prop(String) readonly headerIcon!: string;

  xss = xss;
  isClosed = false;
  WsButtonEnum = WsButtonEnum;

  get buttonLabel() {
    return this.button ? JSON.parse(this.button).label : '';
  }

  get buttonUrl() {
    return this.button ? JSON.parse(this.button).url : '';
  }

  get buttonIcon() {
    return this.button ? JSON.parse(this.button).icon : '';
  }

  get buttonType() {
    if ([UI_VARIANT.DEFAULT, UI_VARIANT.INFO].includes(this.type)) {
      return WsButtonEnum.Type.TERTIARY;
    }

    return WsButtonEnum.Type.TERTIARY_NEUTRAL;
  }

  getIcon() {
    if (this.headerIcon) {
      return this.headerIcon;
    }

    switch (this.type) {
    case UI_VARIANT.NEUTRAL:
    case UI_VARIANT.SUCCESS:
      return 'success_full';
    case UI_VARIANT.WARNING:
      return 'alert_triangle_full';
    case UI_VARIANT.ERROR:
      return 'alert_circle_full';
    case UI_VARIANT.DEFAULT:
    default:
      return 'info_full';
    }
  }

  closeBanner() {
    this.isClosed = true;
    this.$emit('close');
  }
}

export default WsStateBanner;
</script>

<style lang="scss" scoped>
.ws-state-banner {
  display: flex;
  padding: map-get($space-md, y);
  border: $ws-border;
  border-radius: $state-banner-border-radius;
  color: $ws-text-color-dark;

  --state-banner-type-color: #{$ws-text-color-dark};

  // For success/error messages in multi-step forms
  .multi-step .form-step__container > .admin-notifier & {
    margin-top: map-get($space-3xl-b, y);
    margin-bottom: map-get($space-lg, y);
  }

  &__inner-wrapper {
    grid-area: content;

    ::v-deep( > .ws-icon) {
      align-self: flex-start;
      margin-top: map-get($space-xs, y);
      margin-right: map-get($space-md, x);
      color: var(--state-banner-type-color)
    }
  }

  &__content {
    flex: 1;
    word-break: break-word;
  }

  &__title {
    font-weight: map-get($text-weights, bold);
    font-size: 16px;
    line-height: 32px;
  }

  &__text {
    margin: 0;
    font-size: 14.224px;
    line-height: 20px;

    &--no-title {
      padding-top: 6px;
      padding-bottom: 4px;
    }

    ::v-deep > a {
      color: $primary;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__closable {
    position: relative;
    top: -8px;
    left: 8px;
    padding-left: map-get($space-md, x);
  }

  // Sizes
  &.large {
    padding: map-get($space-md, y) map-get($space-md, x);
  }

  &.medium {
    padding: map-get($space-sm, y) map-get($space-md, x);
  }

  &.small {
    padding: map-get($space-xs, y) map-get($space-sm, x);
  }

  // Types

  &.default, &.info {
    background: $info-message-bg;
    border-color: $info-message-border;

    .ws-state-banner__title, a.q-btn.ws-button {
      color: $state-banner-title-color;
    }

    ::v-deep(.ws-icon) {
      --state-banner-type-color: #{$ws-king-200};
    }

    .ws-state-banner__title {
      color: $ws-king-200;
    }
  }

  &.success {
    background: $success-message-bg;
    border-color: $success-message-icon;

    ::v-deep(.ws-icon) {
      --state-banner-type-color: #{$success-message-icon};
    }
  }

  &.error {
    background: $error-message-bg;
    border-color: $error-message-border;
    --state-banner-type-color: #{$error-message-text};

    .ws-state-banner__title {
      color: $error-message-text;
    }

    .ws-state-banner__text {
      color: $ws-text-color-dark;
    }

    ::v-deep(.ws-button) {
      color: $error-message-button;
    }
  }

  &.neutral, &.gray {
    background: $neutral-message-bg;
    border-color: $neutral-message-border;

    ::v-deep(.ws-icon) {
      --state-banner-type-color: #{$success-message-icon};
    }

    ::v-deep(.ws-button) {
      color: $ws-king-200;
    }
  }

  &.warning {
    background: $warning-message-bg;
    border-color: $warning-message-border;

    ::v-deep(.ws-icon) {
      --state-banner-type-color: #{$ws-pumpkin-200};
    }

    .ws-state-banner__title {
      color: $ws-pumpkin-200;
    }
  }

  ::v-deep(.ws-button) {
    font-weight: map-get($text-weights, regular);
  }

  // Deprecated
  &.alert {
    background: $error-message-bg;
    border-color: $error-message-border;

    a.q-btn.ws-button {
      color: $error-message-text;
    }
  }
}

.wsk-root.wsk-app {
  .cart-item-info-box::v-deep {
    .ws-state-banner {
      &__title {
        font-size: 16px;
        line-height: 24px;
      }
      &__text {
        font-size: 14.224px;
        line-height: 20.34px;
      }
    }
  }
}
</style>
